import { useCallback, useEffect, useState } from 'react'
import { UserServiceV4 } from 'services'
import { User } from 'models'
import { registerAccountResetHook } from 'lib/accountSession'
import { isStaleAccountSessionError } from 'lib/accountSessionState'

const cache = new Map<string, User>()
const inflight = new Map<string, Promise<void>>()

// Open screens are deliberately not told: the members they are showing belong to the account just left.
registerAccountResetHook(() => {
  cache.clear()
  inflight.clear()
})

/* Remembers members a screen already has in hand, such as search results, so nothing here goes back
 * to the server for someone the app was just given. */
export const seedUsers = (users: User[]): void => {
  users.forEach((user) => cache.set(user.id, user))
}

/* Waits for every request that covers these members, including ones another screen started. */
const fetchMissing = async (ids: string[]): Promise<void> => {
  const missing = ids.filter((id) => !cache.has(id) && !inflight.has(id))
  const pending = new Set(ids.map((id) => inflight.get(id)).filter(Boolean) as Promise<void>[])

  if (missing.length > 0) {
    const request: Promise<void> = (async () => {
      try {
        seedUsers(await UserServiceV4.getUsersByIds(missing))
      } catch (e) {
        // Switching accounts abandons whatever it had asked for, which is not a failure worth reporting.
        if (!isStaleAccountSessionError(e)) console.error('[useUsersLookup] id lookup failed', e)
      } finally {
        // An account switch clears this list mid-request, so only remove members this request still claims.
        missing.forEach((id) => {
          if (inflight.get(id) === request) inflight.delete(id)
        })
      }
    })()
    missing.forEach((id) => inflight.set(id, request))
    pending.add(request)
  }

  if (pending.size > 0) await Promise.all(pending)
}

export const useUsersLookup = (ids: string[]) => {
  const [tick, setTick] = useState(0)
  const [loaded, setLoaded] = useState(false)

  // Sorted, so the same members arriving in a different order don't look like a new set to resolve.
  const idsKey = ids.slice().sort().join(',')

  useEffect(() => {
    if (ids.length === 0) {
      setLoaded(true)
      return
    }
    let cancelled = false
    setLoaded(false)
    fetchMissing(ids).then(() => {
      if (cancelled) return
      setLoaded(true)
      setTick((t) => t + 1)
    })
    return () => {
      cancelled = true
    }
  }, [idsKey])

  const getUser = useCallback((id: string | null | undefined): User | undefined => (id ? cache.get(id) : undefined), [
    tick
  ])

  const getUserLabel = useCallback(
    (id: string | null | undefined): string => {
      if (!id) return '—'
      const user = cache.get(id)
      return user?.name?.trim() || user?.email?.trim() || id
    },
    [tick]
  )

  return { loaded, getUser, getUserLabel }
}
